/**
 * Per-client Google feeds + OAuth.
 *
 * - "Primary Google account" section: kicks off OAuth that grants every
 *   Workspace + Marketing scope at once (Ads, GA4, GSC, GBP, YouTube).
 *   The grant is tagged with this client's `client_id` so downstream
 *   data fetches scope to this client only.
 * - Separate "YouTube accounts" section: lets admins attach one or many
 *   YouTube/brand accounts on top of the primary grant via repeated OAuth
 *   rounds (each tagged `purpose=youtube`). Useful when a client publishes
 *   under multiple YouTube channels owned by different Google accounts.
 * - Each connection shows the connected mailbox, scopes, and a Disconnect
 *   button so the admin can prune stale grants.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import {
  Chrome, Youtube, CheckCircle2, Loader2, RefreshCw, Trash2, Plus,
  BarChart3, Megaphone, Search as SearchIcon, MapPin, FileVideo,
  ShieldCheck, ShieldAlert, ShieldQuestion, AlertCircle, ChevronDown,
  ChevronRight,
} from "lucide-react";

const PRIMARY_SERVICES = [
  { icon: BarChart3, color: "#F9AB00", label: "Google Analytics (GA4)" },
  { icon: Megaphone, color: "#4285F4", label: "Google Ads" },
  { icon: MapPin, color: "#34A853", label: "Google Business Profile" },
  { icon: SearchIcon, color: "#4285F4", label: "Search Console" },
];

export default function ClientGoogleFeedsPage() {
  const { clientId } = useParams();
  const [busy, setBusy] = useState(null);     // "primary" | "youtube" | null
  const [groups, setGroups] = useState(null); // { primary: [...], youtube: [...] }
  const [loadError, setLoadError] = useState(null);
  const [verifying, setVerifying] = useState({}); // { [purpose::email]: true }
  const [resources, setResources] = useState({}); // { [purpose::email]: {resources, loading, error} }
  const [businessUnits, setBusinessUnits] = useState([]); // client's properties
  const [assignments, setAssignments] = useState({}); // { 'service::resource_id': {status, business_unit_id} }

  const [refreshing, setRefreshing] = useState(false);

  // Lazy resource loader — called once per row after the connections list
  // renders. Backend caches for 30 min so repeat page loads are cheap.
  // NOTE: this MUST be declared BEFORE `refresh` — the latter's
  // useCallback dependency array references `loadResources`, and JS
  // temporal-dead-zone rules would otherwise throw
  // "Cannot access 'loadResources' before initialization" on first
  // render.
  const loadResources = useCallback(async (purpose, email, force = false) => {
    const key = `${purpose}::${email}`;
    setResources((prev) => ({
      ...prev, [key]: { ...(prev[key] || {}), loading: true },
    }));
    try {
      const { data } = await api.get(
        `/clients/${clientId}/google/connections/${encodeURIComponent(email)}/resources`,
        { params: { purpose, force: force ? "true" : "false" } },
      );
      setResources((prev) => ({
        ...prev,
        [key]: { resources: data.resources || {}, fetched_at: data.fetched_at,
                 cached: !!data.cached, loading: false },
      }));
    } catch (e) {
      setResources((prev) => ({
        ...prev,
        [key]: { resources: {}, loading: false,
                 error: e.response?.data?.detail || "Failed to load resources" },
      }));
    }
  }, [clientId]);

  const refresh = useCallback(async () => {
    setLoadError(null);
    setRefreshing(true);
    try {
      const [conn, bu, asg] = await Promise.all([
        api.get(`/clients/${clientId}/google/connections`),
        api.get(`/clients/${clientId}/business-units`).catch(() => ({ data: [] })),
        api.get(`/clients/${clientId}/google/resource-assignments`).catch(() => ({ data: { assignments: {} } })),
      ]);
      const nextGroups = conn.data?.connections || { primary: [], youtube: [] };
      setGroups(nextGroups);
      setBusinessUnits(bu.data || []);
      setAssignments(asg.data?.assignments || {});

      // Also force-refresh the per-connection resource caches (Analytics,
      // Search Console, Ads, GMB, YouTube…). That's what actually
      // powers the reviews sync + property assignment pickers, so the
      // Refresh button now has an observable effect for the user.
      const emailsPrimary = (nextGroups.primary || [])
        .map((r) => r.google_email).filter(Boolean);
      const emailsYT = (nextGroups.youtube || [])
        .map((r) => r.google_email).filter(Boolean);
      await Promise.all([
        ...emailsPrimary.map((e) => loadResources("primary", e, true)),
        ...emailsYT.map((e) => loadResources("youtube", e, true)),
      ]);
      const totalConns = emailsPrimary.length + emailsYT.length;
      if (totalConns > 0) {
        toast.success(`Refreshed ${totalConns} Google connection${totalConns === 1 ? "" : "s"}`);
      } else {
        toast.info("No Google account is connected yet — click 'Connect' below.");
      }
    } catch (e) {
      logger.warn("Failed to load Google connections:", e?.response?.data);
      setLoadError(e?.response?.data?.detail || "Failed to load connections");
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  }, [clientId, loadResources]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-fetch resources for every connected row whenever the groups list
  // refreshes — backend cache makes repeat calls effectively free.
  useEffect(() => {
    if (!groups) return;
    const all = [
      ...(groups.primary || []).map((r) => ["primary", r.google_email]),
      ...(groups.youtube || []).map((r) => ["youtube", r.google_email]),
    ];
    all.forEach(([p, email]) => { if (email) loadResources(p, email); });
  }, [groups, loadResources]);

  // Detect ?connected=1&purpose=… after the OAuth callback bounce.
  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.searchParams.get("connected") === "1") {
      const purpose = u.searchParams.get("purpose") || "primary";
      toast.success(
        purpose === "youtube"
          ? "YouTube account connected"
          : "Google account connected — all assets ready to fetch.",
      );
      // Clean the URL so refreshes don't re-fire the toast.
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const startOAuth = useCallback(async (purpose, opts = {}) => {
    setBusy(purpose);
    try {
      const payload = { purpose };
      if (opts.login_hint) payload.login_hint = opts.login_hint;
      const { data } = await api.post(
        `/clients/${clientId}/google/oauth/start`, payload);
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start Google OAuth");
      setBusy(null);
    }
  }, [clientId]);

  const disconnect = async (email, purpose) => {
    const label = purpose === "youtube" ? "YouTube account" : "primary Google account";
    if (!window.confirm(`Disconnect ${email} (${label}) from this client?`)) return;
    try {
      await api.delete(
        `/clients/${clientId}/google/connections/${encodeURIComponent(email)}`,
        { params: { purpose } },
      );
      toast.success("Disconnected");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    }
  };

  const verify = async (email, purpose) => {
    const key = `${purpose}::${email}`;
    setVerifying((v) => ({ ...v, [key]: true }));
    try {
      const { data } = await api.post(
        `/clients/${clientId}/google/connections/${encodeURIComponent(email)}/verify`,
        null, { params: { purpose } },
      );
      if (data.ok) {
        toast.success(`Verified · ${data.detail || "reachable"}`);
      } else {
        toast.error(`Verification failed · ${data.detail || "unknown error"}`);
      }
      await refresh();
      await loadResources(purpose, email, true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying((v) => {
        const next = { ...v }; delete next[key]; return next;
      });
    }
  };

  const assignResource = useCallback(async (service, resource_id, resource_label,
                                            business_unit_id, status) => {
    const key = `${service}::${resource_id}`;
    // Optimistic update — snap the dropdown to the new value immediately.
    setAssignments((prev) => {
      const next = { ...prev };
      if (status === "unassigned") {
        delete next[key];
      } else {
        next[key] = {
          service, resource_id, resource_label,
          business_unit_id: status === "assigned" ? business_unit_id : null,
          status,
        };
      }
      return next;
    });
    try {
      await api.post(`/clients/${clientId}/google/resource-assignments`, {
        service, resource_id, resource_label,
        business_unit_id: status === "assigned" ? business_unit_id : null,
        status,
      });
      toast.success(
        status === "assigned" ? "Assigned to property"
          : status === "ignored" ? "Ignored — excluded from reports"
          : "Cleared",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
      // Rollback via authoritative refetch.
      try {
        const { data } = await api.get(`/clients/${clientId}/google/resource-assignments`);
        setAssignments(data?.assignments || {});
      } catch (_) { /* ignore */ }
    }
  }, [clientId]);

  // Refresh button on a single ConnectionGroup — force-refetches resources
  // for every connected row inside that group.
  const refreshGroup = useCallback(async (purpose) => {
    const rows = groups?.[purpose] || [];
    await Promise.all(
      rows
        .map((r) => r.google_email)
        .filter(Boolean)
        .map((email) => loadResources(purpose, email, true)),
    );
    if (rows.length > 0) toast.success("Refreshed");
  }, [groups, loadResources]);

  return (
    <div className="space-y-6" data-testid="client-google-feeds-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Connections · Google</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">
            Google feeds
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
            Connect the Google account(s) that own this client&apos;s Workspace and
            Marketing assets. The primary grant covers Analytics, Ads, Search
            Console and Business Profile in one OAuth round. Add extra YouTube /
            brand accounts below if the client publishes under more than one channel.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={refresh}
          disabled={refreshing}
          className="rounded-sm" data-testid="google-feeds-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {loadError && (
        <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          {loadError}
        </div>
      )}

      {/* Primary Google account */}
      <ConnectionGroup
        title="Primary Google account"
        subtitle="One OAuth grants Analytics, Ads, Business Profile and Search Console."
        icon={<Chrome className="h-5 w-5 text-[#4285F4]" />}
        color="#4285F4"
        services={PRIMARY_SERVICES}
        rows={groups?.primary || []}
        loading={!groups}
        busy={busy === "primary"}
        onConnect={() => startOAuth("primary")}
        onReconnect={(email) => startOAuth("primary", { login_hint: email })}
        onDisconnect={(email) => disconnect(email, "primary")}
        onVerify={(email) => verify(email, "primary")}
        onRefreshAssets={() => refreshGroup("primary")}
        verifying={verifying}
        resources={resources}
        businessUnits={businessUnits}
        assignments={assignments}
        onAssign={assignResource}
        purpose="primary"
        ctaLabel="Connect primary Google account"
        emptyHint="No primary account connected yet — clicking Connect will redirect to Google's consent screen. Sign in with the client's Google email to grant access."
      />

      {/* YouTube accounts */}
      <ConnectionGroup
        title="YouTube · brand accounts"
        subtitle="Attach one or more YouTube/brand accounts. The primary grant above already includes YouTube — add extras only if this client publishes under different Google identities."
        icon={<Youtube className="h-5 w-5 text-[#FF0000]" />}
        color="#FF0000"
        services={[{ icon: FileVideo, color: "#FF0000", label: "YouTube Data + Analytics" }]}
        rows={groups?.youtube || []}
        loading={!groups}
        busy={busy === "youtube"}
        onConnect={() => startOAuth("youtube")}
        onReconnect={(email) => startOAuth("youtube", { login_hint: email })}
        onDisconnect={(email) => disconnect(email, "youtube")}
        onVerify={(email) => verify(email, "youtube")}
        onRefreshAssets={() => refreshGroup("youtube")}
        verifying={verifying}
        resources={resources}
        businessUnits={businessUnits}
        assignments={assignments}
        onAssign={assignResource}
        purpose="youtube"
        ctaLabel="Connect another YouTube account"
        emptyHint="No additional YouTube accounts attached. The primary Google account above already grants YouTube access — only add another if this client publishes under a separate Google identity (e.g. a brand channel)."
      />
    </div>
  );
}

function ConnectionGroup({ title, subtitle, icon, color, services, rows,
                          loading, busy, onConnect, onReconnect, onDisconnect,
                          onVerify, onRefreshAssets, verifying, resources,
                          businessUnits, assignments, onAssign, purpose,
                          ctaLabel, emptyHint }) {
  const isYoutube = title.toLowerCase().includes("youtube");
  const testidSuffix = isYoutube ? "youtube" : "primary";
  return (
    <Card className="rounded-sm border-zinc-200 overflow-hidden">
      <div className="h-1.5" style={{ background: color }} />
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-sm flex items-center justify-center"
              style={{ background: `${color}14` }}>
              {icon}
            </div>
            <div>
              <h2 className="font-display font-bold text-xl">{title}</h2>
              <p className="text-[12px] text-zinc-500 mt-0.5 max-w-2xl">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {rows.length > 0 && onRefreshAssets && (
              <Button size="sm" variant="outline"
                className="rounded-sm whitespace-nowrap"
                onClick={onRefreshAssets}
                data-testid={`google-refresh-assets-${testidSuffix}`}
                title="Re-fetch the live asset list from Google for every connected account in this section">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh assets
              </Button>
            )}
            <Button onClick={onConnect} disabled={busy}
              className="rounded-sm text-white whitespace-nowrap"
              style={{ background: color }}
              data-testid={`google-connect-${testidSuffix}`}>
              {busy
                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                : <Plus className="h-4 w-4 mr-2" />}
              {ctaLabel}
            </Button>
          </div>
        </div>

        {/* Service chips — visual summary of what this grant unlocks */}
        <div className="flex flex-wrap gap-2">
          {services.map((s) => {
            const SIcon = s.icon;
            return (
              <span key={s.label}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-zinc-200 bg-zinc-50 text-[11px] text-zinc-700">
                <SIcon className="h-3.5 w-3.5" style={{ color: s.color }} />
                {s.label}
              </span>
            );
          })}
        </div>

        {/* Connected accounts list */}
        {loading ? (
          <div className="text-[12px] text-zinc-500 flex items-center gap-2 py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading connections…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-sm border border-dashed border-zinc-200 bg-zinc-50/40 p-4 text-[12px] text-zinc-600">
            {emptyHint}
          </div>
        ) : (
          <div className="space-y-3"
            data-testid={`google-connections-${testidSuffix}`}>
            {rows.map((r) => {
              const vKey = `${purpose}::${r.google_email}`;
              const isVerifying = !!(verifying && verifying[vKey]);
              const rState = (resources && resources[vKey]) || {};
              const ytChannels = rState.resources?.channels || [];
              const ytPrimaryChannel = ytChannels[0];
              return (
                <div key={r.google_email}
                  className="rounded-sm border border-zinc-200 overflow-hidden">
                  {/* Row header — email + channel branding (youtube) + actions */}
                  <div className="flex items-center justify-between gap-3 p-3 hover:bg-zinc-50/50">
                    <div className="flex items-center gap-3 min-w-0">
                      {purpose === "youtube" && ytPrimaryChannel?.thumbnail ? (
                        <img src={ytPrimaryChannel.thumbnail} alt=""
                          className="h-9 w-9 rounded-sm object-cover border border-zinc-200 shrink-0" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      )}
                      <div className="min-w-0">
                        {purpose === "youtube" && ytPrimaryChannel?.title && (
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Youtube className="h-3.5 w-3.5 text-[#FF0000] shrink-0" />
                            <span className="font-display font-bold text-sm truncate">
                              {ytPrimaryChannel.title}
                            </span>
                            {ytPrimaryChannel.subscriber_count > 0 && (
                              <span className="text-[10px] font-mono text-zinc-500 whitespace-nowrap">
                                · {Number(ytPrimaryChannel.subscriber_count).toLocaleString()} subs
                              </span>
                            )}
                          </div>
                        )}
                        <div className="font-semibold text-sm truncate"
                          data-testid={`google-row-email-${purpose}-${r.google_email}`}>
                          {r.google_email || "(unknown email)"}
                        </div>
                        <div className="text-[11px] font-mono text-zinc-500 truncate">
                          {(r.scopes || []).length} scope{r.scopes?.length === 1 ? "" : "s"} ·
                          Connected {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <HealthPill row={r} />
                      {(() => {
                        // Token looks broken if the last verification failed
                        // with an unauthorised-style error. Show Reconnect
                        // instead of hiding the issue behind Verify.
                        const detail = (r.last_verify_detail || "").toLowerCase();
                        const brokenAuth = r.last_verify_status
                          && r.last_verify_status !== "ok"
                          && (detail.includes("invalid_grant")
                              || detail.includes("invalid grant")
                              || detail.includes("expired")
                              || detail.includes("unauthorized")
                              || detail.includes("unauthorised")
                              || detail.includes("401")
                              || detail.includes("token") /* generic catch */);
                        if (!brokenAuth) return null;
                        return (
                          <Button size="sm"
                            className="rounded-sm h-7 px-2 bg-[#4285F4] hover:bg-[#3367d6] text-white"
                            onClick={() => onReconnect(r.google_email)}
                            data-testid={`google-reconnect-${purpose}-${r.google_email}`}
                            title="Token expired — re-run OAuth for this account">
                            <Chrome className="h-3.5 w-3.5" />
                            <span className="ml-1.5 text-[11px]">Reconnect</span>
                          </Button>
                        );
                      })()}
                      <Button size="sm" variant="outline" className="rounded-sm h-7 px-2"
                        onClick={() => onVerify(r.google_email)}
                        disabled={isVerifying}
                        data-testid={`google-verify-${purpose}-${r.google_email}`}
                        title="Probe Google API to confirm the refresh token still works">
                        {isVerifying
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <RefreshCw className="h-3.5 w-3.5" />}
                        <span className="ml-1.5 text-[11px]">Verify</span>
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 rounded-sm"
                        onClick={() => onDisconnect(r.google_email)}
                        data-testid={`google-disconnect-${purpose}-${r.google_email}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Collapsible service sections — Ads → GA4 → GBP → SC for
                      primary, or Channels for youtube — each with a property
                      dropdown next to every asset. */}
                  <ResourcesPanel purpose={purpose} email={r.google_email}
                    state={rState} businessUnits={businessUnits}
                    assignments={assignments} onAssign={onAssign} />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResourcesPanel({ purpose, email, state, businessUnits, assignments, onAssign }) {
  if (!state) return null;
  if (state.loading && !state.resources) {
    return (
      <div className="border-t border-zinc-100 bg-zinc-50/40 px-3 py-2.5 text-[11px] text-zinc-500 flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Discovering assets…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="border-t border-zinc-100 bg-red-50/60 px-3 py-2.5 text-[11px] text-red-800 flex items-center gap-2">
        <AlertCircle className="h-3 w-3" /> {state.error}
      </div>
    );
  }
  const res = state.resources || {};
  const tokenError = res.token_error;

  if (purpose === "youtube") {
    const channels = res.channels || [];
    const sections = [
      {
        key: "youtube",
        label: "YouTube channels",
        icon: <Youtube className="h-3.5 w-3.5 text-[#FF0000]" />,
        items: channels,
        error: res.channels_error,
        renderItem: (c) => (
          <div className="flex items-center gap-2 min-w-0">
            {c.thumbnail && (
              <img src={c.thumbnail} alt=""
                className="h-7 w-7 rounded-sm object-cover border border-zinc-200 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="font-display font-bold text-[12px] truncate">{c.title || "(untitled)"}</div>
              <div className="text-[10px] font-mono text-zinc-500">
                {Number(c.subscriber_count || 0).toLocaleString()} subs ·
                {" "}{Number(c.video_count || 0).toLocaleString()} videos
              </div>
            </div>
          </div>
        ),
        resourceId: (c) => c.channel_id,
        resourceLabel: (c) => c.title,
      },
    ];
    return (
      <CollapsibleSections email={email} sections={sections}
        businessUnits={businessUnits} assignments={assignments}
        onAssign={onAssign} tokenError={tokenError}
        testidPrefix={`google-resources-youtube-${email}`} />
    );
  }

  // Primary — Google Ads → GA4 → GBP → Search Console (top-to-bottom per spec)
  const sections = [
    {
      key: "google_ads",
      label: "Google Ads",
      icon: <Megaphone className="h-3.5 w-3.5" style={{ color: "#4285F4" }} />,
      items: res.google_ads || [],
      error: res.google_ads_error,
      renderItem: (c) => {
        const formattedId = formatGoogleAdsCustomerId(c.customer_id);
        const displayName = c.descriptive_name || formattedId;
        return (
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-7 w-7 rounded-sm flex items-center justify-center shrink-0"
              style={{ background: "#4285F414" }}>
              <Megaphone className="h-3.5 w-3.5" style={{ color: "#4285F4" }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                <span className="font-display font-bold text-[12px] truncate">
                  {displayName}
                </span>
                {c.is_manager && (
                  <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-sm bg-zinc-200 text-zinc-700">
                    MCC
                  </span>
                )}
              </div>
              <div className="text-[10px] font-mono text-zinc-500 truncate flex items-center gap-1.5 flex-wrap">
                <span>{formattedId}</span>
                {c.currency_code && <span>· {c.currency_code}</span>}
                {c.time_zone && <span>· {c.time_zone}</span>}
              </div>
            </div>
          </div>
        );
      },
      resourceId: (c) => String(c.customer_id),
      resourceLabel: (c) => c.descriptive_name || `Customer ${c.customer_id}`,
    },
    {
      key: "analytics_ga4",
      label: "Google Analytics (GA4)",
      icon: <BarChart3 className="h-3.5 w-3.5" style={{ color: "#F9AB00" }} />,
      items: res.analytics_ga4 || [],
      error: res.analytics_ga4_error,
      renderItem: (p) => (
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-mono text-[12px] truncate">{p.display_name || p.property_resource}</span>
          <span className="text-[10px] font-mono text-zinc-500">ID {p.property_id}</span>
        </div>
      ),
      resourceId: (p) => String(p.property_id),
      resourceLabel: (p) => p.display_name || p.property_resource,
    },
    {
      key: "gmb",
      label: "Google Business Profile",
      icon: <MapPin className="h-3.5 w-3.5" style={{ color: "#34A853" }} />,
      items: res.gmb || [],
      error: res.gmb_error,
      renderItem: (l) => (
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-mono text-[12px] truncate">{l.location_title || "(untitled)"}</span>
          <span className="text-[10px] font-mono text-zinc-500 truncate">{l.account_name}</span>
        </div>
      ),
      resourceId: (l) => String(l.location_id),
      resourceLabel: (l) => l.location_title || l.account_name || "GMB location",
    },
    {
      key: "search_console",
      label: "Search Console",
      icon: <SearchIcon className="h-3.5 w-3.5" style={{ color: "#4285F4" }} />,
      items: res.search_console || [],
      error: res.search_console_error,
      renderItem: (s) => (
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-mono text-[12px] truncate">{s.site_url}</span>
          <span className="text-[10px] font-mono text-zinc-500 uppercase">{s.type}</span>
        </div>
      ),
      resourceId: (s) => String(s.site_url),
      resourceLabel: (s) => s.site_url,
    },
  ];
  return (
    <CollapsibleSections email={email} sections={sections}
      businessUnits={businessUnits} assignments={assignments}
      onAssign={onAssign} tokenError={tokenError}
      testidPrefix={`google-resources-primary-${email}`} />
  );
}

function CollapsibleSections({ email, sections, businessUnits, assignments,
                               onAssign, tokenError, testidPrefix }) {
  // Open the first section that has assets by default — keeps the page
  // dense (most clients have at most one or two services with assets).
  const [open, setOpen] = useState(() => {
    const init = {};
    const first = sections.find((s) => (s.items?.length || 0) > 0);
    if (first) init[first.key] = true;
    return init;
  });

  const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="border-t border-zinc-100 bg-zinc-50/40"
      data-testid={testidPrefix}>
      {tokenError && (
        <div className="px-3 py-2 text-[11px] text-red-800 bg-red-50 border-b border-red-100 flex items-start gap-1.5">
          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
          <span className="break-words">{tokenError}</span>
        </div>
      )}
      <div className="divide-y divide-zinc-100">
        {sections.map((sec) => {
          const isOpen = !!open[sec.key];
          const count = sec.items?.length || 0;
          return (
            <div key={sec.key} data-testid={`google-section-${sec.key}-${email}`}>
              <button type="button" onClick={() => toggle(sec.key)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-100/60 text-left"
                data-testid={`google-section-toggle-${sec.key}-${email}`}>
                {isOpen
                  ? <ChevronDown className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                  : <ChevronRight className="h-3.5 w-3.5 text-zinc-500 shrink-0" />}
                {sec.icon}
                <span className="label-overline text-[11px]">{sec.label}</span>
                <span className="font-mono text-[10px] text-zinc-400">({count})</span>
                {sec.error && (
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-amber-700">
                    <AlertCircle className="h-3 w-3" /> needs attention
                  </span>
                )}
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-1.5">
                  {sec.error ? (
                    <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 flex items-start gap-1.5">
                      <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                      <span className="break-words">{sec.error}</span>
                    </div>
                  ) : count === 0 ? (
                    <div className="text-[11px] italic text-zinc-500">None discovered.</div>
                  ) : (
                    sec.items.map((item, i) => {
                      const rid = sec.resourceId(item) || `idx-${i}`;
                      const rlabel = sec.resourceLabel(item) || "";
                      const aKey = `${sec.key}::${rid}`;
                      const asg = assignments?.[aKey];
                      const selValue = asg?.status === "ignored"
                        ? "__ignored__"
                        : asg?.business_unit_id || "";
                      return (
                        <div key={rid}
                          className="grid grid-cols-[1fr_240px] gap-2 items-center py-1.5 border-b border-zinc-100 last:border-b-0"
                          data-testid={`google-asset-${sec.key}-${rid}`}>
                          {sec.renderItem(item)}
                          <PropertySelect
                            value={selValue}
                            businessUnits={businessUnits}
                            onChange={(v) => {
                              if (v === "") {
                                onAssign(sec.key, rid, rlabel, null, "unassigned");
                              } else if (v === "__ignored__") {
                                onAssign(sec.key, rid, rlabel, null, "ignored");
                              } else {
                                onAssign(sec.key, rid, rlabel, v, "assigned");
                              }
                            }}
                            testid={`google-asset-assign-${sec.key}-${rid}`}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PropertySelect({ value, businessUnits, onChange, testid }) {
  const isIgnored = value === "__ignored__";
  return (
    <select value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`h-7 text-[11px] font-mono rounded-sm border px-1.5 truncate ${
        isIgnored
          ? "border-zinc-300 bg-zinc-100 text-zinc-500"
          : value
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-zinc-200 bg-white text-zinc-700"
      }`}
      title={isIgnored ? "Ignored — excluded from reports" :
             value ? "Assigned to a property" : "Pick a property or ignore"}>
      <option value="">— Unassigned —</option>
      <optgroup label="Assign to property">
        {(businessUnits || []).map((b) => (
          <option key={b.business_unit_id} value={b.business_unit_id}>
            {b.name || b.business_unit_id}
          </option>
        ))}
      </optgroup>
      <option value="__ignored__">Ignore (exclude from reports)</option>
    </select>
  );
}

function HealthPill({ row }) {
  const status = row.last_verify_status;
  const verifiedAt = row.last_verified_at;
  if (!verifiedAt) {
    return (
      <Badge className="rounded-sm bg-zinc-100 text-zinc-600 border-zinc-200 font-mono text-[10px] gap-1"
        data-testid="google-health-pill-unknown"
        title="Never verified — click Verify to probe the Google API.">
        <ShieldQuestion className="h-3 w-3" />
        Not verified
      </Badge>
    );
  }
  const ago = relativeTime(verifiedAt);
  if (status === "ok") {
    return (
      <Badge className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200 font-mono text-[10px] gap-1"
        data-testid="google-health-pill-ok"
        title={`Last verified ${ago} · ${row.last_verify_detail || "reachable"}`}>
        <ShieldCheck className="h-3 w-3" />
        Verified {ago}
      </Badge>
    );
  }
  return (
    <Badge className="rounded-sm bg-red-50 text-red-800 border-red-200 font-mono text-[10px] gap-1"
      data-testid="google-health-pill-error"
      title={row.last_verify_detail || "Verification failed"}>
      <ShieldAlert className="h-3 w-3" />
      Failed {ago}
    </Badge>
  );
}

function formatGoogleAdsCustomerId(cid) {
  // Google Ads customer IDs are 10-digit numbers displayed as 123-456-7890.
  const s = String(cid || "").replace(/\D/g, "");
  if (s.length !== 10) return String(cid || "");
  return `${s.slice(0, 3)}-${s.slice(3, 6)}-${s.slice(6)}`;
}

function relativeTime(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 60_000) return "just now";
    const m = Math.floor(diff / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}
